/**
 * Setpoint Write Dialog Component
 * Handles SCADA tag setpoint writing with validation and confirmation
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Grid,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Edit as EditIcon,
  Security as SecurityIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { SCADATag, SCADAWriteRequest, SCADAWriteResponse } from '../../types/scada';
import { scadaWriteAPI } from '../../services/scadaWriteService';

interface SetpointWriteDialogProps {
  open: boolean;
  onClose: () => void;
  tag: SCADATag | null;
  onWriteSuccess?: (response: SCADAWriteResponse) => void;
}

export const SetpointWriteDialog: React.FC<SetpointWriteDialogProps> = ({
  open,
  onClose,
  tag,
  onWriteSuccess,
}) => {
  const [newValue, setNewValue] = useState<string>('');
  const [writeReason, setWriteReason] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState<string>('');
  const [acknowledgeWarning, setAcknowledgeWarning] = useState(false);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open && tag) {
      setNewValue(tag.current_value?.toString() || '');
      setWriteReason('');
      setError(null);
      setShowConfirmation(false);
      setAcknowledgeWarning(false);
    }
  }, [open, tag]);

  const handleClose = () => {
    setShowConfirmation(false);
    onClose();
  };

  const validateValue = (): { valid: boolean; error?: string } => {
    if (!tag) return { valid: false, error: 'No tag selected' };

    const numValue = parseFloat(newValue);
    if (isNaN(numValue)) {
      return { valid: false, error: 'Please enter a valid number' };
    }

    if (tag.write_min_value !== null && numValue < tag.write_min_value) {
      return { valid: false, error: `Value must be >= ${tag.write_min_value}` };
    }

    if (tag.write_max_value !== null && numValue > tag.write_max_value) {
      return { valid: false, error: `Value must be <= ${tag.write_max_value}` };
    }

    return { valid: true };
  };

  const checkWriteSession = async (): Promise<boolean> => {
    if (!tag?.site) return false;
    
    try {
      const status = await scadaWriteAPI.getWriteSessionStatus(tag.site);
      return status.session_active && (status.remaining_seconds === null || status.remaining_seconds > 0);
    } catch (err) {
      console.error('Failed to check write session:', err);
    }
    
    return false;
  };

  const handleWriteAttempt = async (forceWrite: boolean = false) => {
    if (!tag) return;

    const validation = validateValue();
    if (!validation.valid) {
      setError(validation.error || 'Invalid value');
      return;
    }

    // Check if we have an active write session
    const hasSession = await checkWriteSession();
    if (!hasSession) {
      setError('No active write session. Please enable control first.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const writeRequest: SCADAWriteRequest = {
        new_value: parseFloat(newValue),
        write_reason: writeReason,
        force_write: forceWrite,
      };

      const response = await scadaWriteAPI.writeSetpoint(tag.id, writeRequest);

      if (response.success) {
        // Write was successful
        onWriteSuccess?.(response);
        handleClose();
      } else if (response.requires_confirmation && !forceWrite) {
        // Confirmation required
        setConfirmationMessage(response.confirmation_message || 'Are you sure you want to proceed with this write operation?');
        setShowConfirmation(true);
      } else {
        // Write failed
        setError(response.error || 'Write operation failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to communicate with SCADA system');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmedWrite = () => {
    setShowConfirmation(false);
    handleWriteAttempt(true); // Force write on confirmation
  };

  const getWriteAvailabilityStatus = () => {
    if (!tag) return null;

    if (!tag.is_writable) {
      return {
        available: false,
        reason: 'Tag is not configured for write operations',
        icon: <BlockIcon color="error" />,
        color: 'error' as const,
      };
    }

    if (!tag.write_available) {
      return {
        available: false,
        reason: tag.write_disabled_reason || 'Write operations not available',
        icon: <BlockIcon color="error" />,
        color: 'error' as const,
      };
    }

    // Check control switch status
    if (tag.control_switch_status === 'local') {
      return {
        available: false,
        reason: 'Equipment is in LOCAL control mode',
        icon: <SecurityIcon color="warning" />,
        color: 'warning' as const,
      };
    }

    if (tag.control_switch_status === 'unknown') {
      return {
        available: false,
        reason: 'Control switch status unknown',
        icon: <WarningIcon color="warning" />,
        color: 'warning' as const,
      };
    }

    return {
      available: true,
      reason: 'Write operations available',
      icon: <CheckIcon color="success" />,
      color: 'success' as const,
    };
  };

  const writeStatus = getWriteAvailabilityStatus();

  if (!tag) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <EditIcon />
          Write Setpoint - {tag.tag_name}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* Current Tag Information */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Current Tag Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Description
                    </Typography>
                    <Typography variant="body1">{tag.description}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Current Value
                    </Typography>
                    <Typography variant="body1">
                      {tag.current_value_display} {tag.engineering_unit}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Data Type
                    </Typography>
                    <Typography variant="body1">{tag.data_type}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Permission Level Required
                    </Typography>
                    <Chip 
                      label={tag.write_permission_level || 'operator'} 
                      size="small" 
                      color="primary"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Write Availability Status */}
          <Grid item xs={12}>
            <Alert 
              severity={writeStatus?.color} 
              icon={writeStatus?.icon}
              sx={{ mb: 2 }}
            >
              {writeStatus?.reason}
            </Alert>
          </Grid>

          {/* Write Form - only show if write is available */}
          {writeStatus?.available && (
            <>
              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="New Setpoint Value"
                  type="number"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  InputProps={{
                    endAdornment: tag.engineering_unit,
                  }}
                  helperText={
                    tag.write_min_value !== null || tag.write_max_value !== null
                      ? `Range: ${tag.write_min_value ?? 'no min'} to ${tag.write_max_value ?? 'no max'}`
                      : 'No range limits configured'
                  }
                  disabled={isLoading}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Reason for Change"
                  value={writeReason}
                  onChange={(e) => setWriteReason(e.target.value)}
                  placeholder="Optional: reason for setpoint change"
                  disabled={isLoading}
                  multiline
                  rows={2}
                />
              </Grid>

              {/* Safety Warning */}
              {tag.requires_confirmation && (
                <Grid item xs={12}>
                  <Alert severity="warning" icon={<WarningIcon />}>
                    <Typography variant="body2">
                      {tag.confirmation_message || 'This tag requires confirmation before writing.'}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={acknowledgeWarning}
                          onChange={(e) => setAcknowledgeWarning(e.target.checked)}
                        />
                      }
                      label="I acknowledge this warning and wish to proceed"
                    />
                  </Alert>
                </Grid>
              )}

              {/* Error Display */}
              {error && (
                <Grid item xs={12}>
                  <Alert severity="error" icon={<ErrorIcon />}>
                    {error}
                  </Alert>
                </Grid>
              )}
            </>
          )}
        </Grid>

        {/* Confirmation Dialog */}
        <Dialog open={showConfirmation} onClose={() => setShowConfirmation(false)}>
          <DialogTitle>Confirm Setpoint Write</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {confirmationMessage}
            </Alert>
            <Typography variant="body1">
              Are you sure you want to change {tag.tag_name} from{' '}
              <strong>{tag.current_value_display}</strong> to{' '}
              <strong>{newValue}</strong> {tag.engineering_unit}?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowConfirmation(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmedWrite} 
              variant="contained" 
              color="warning"
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={20} /> : 'Confirm Write'}
            </Button>
          </DialogActions>
        </Dialog>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        {writeStatus?.available && (
          <Button
            onClick={() => handleWriteAttempt(false)}
            variant="contained"
            color="primary"
            disabled={
              isLoading ||
              !newValue ||
              !validateValue().valid ||
              (tag.requires_confirmation && !acknowledgeWarning)
            }
            startIcon={isLoading ? <CircularProgress size={16} /> : <EditIcon />}
          >
            {isLoading ? 'Writing...' : 'Write Setpoint'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
